import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  BookOpen,
  GitBranch,
  DollarSign,
  FileText,
  Code,
  Wrench,
  AlertTriangle,
  Send,
} from 'lucide-react';
import {
  adminApi,
  type AdminLetterInDetail,
  type AdminLetterInExtraction,
} from '../../lib/api';
import {
  LetterInspector,
  PipelineTimeline,
  CostBreakdown,
  type PipelineStageData,
} from '../../components/admin/letter-review';

type DetailTab = 'letter' | 'pipeline' | 'cost' | 'delta' | 'merge' | 'raw' | 'actions';

export function InboundMailDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [letter, setLetter] = useState<AdminLetterInDetail | null>(null);
  const [extraction, setExtraction] = useState<AdminLetterInExtraction | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isReExtracting, setIsReExtracting] = useState(false);
  const [reExtractReason, setReExtractReason] = useState('');
  const [activeTab, setActiveTab] = useState<DetailTab>('letter');
  const [costDetail, setCostDetail] = useState<Awaited<
    ReturnType<typeof adminApi.getLetterCost>
  > | null>(null);
  const [costNotFound, setCostNotFound] = useState(false);
  const [isCostLoading, setIsCostLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const [letterData, extractionData] = await Promise.all([
        adminApi.getLetterIn(id),
        adminApi.getLetterInExtraction(id),
      ]);
      setLetter(letterData);
      setExtraction(extractionData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load letter');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch cost data
  useEffect(() => {
    const loadCost = async () => {
      if (!id) return;
      setIsCostLoading(true);
      setCostNotFound(false);
      try {
        const data = await adminApi.getLetterCost(id);
        setCostDetail(data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '';
        if (errorMessage.includes('No cost data') || errorMessage.includes('404')) {
          setCostNotFound(true);
        }
        setCostDetail(null);
      } finally {
        setIsCostLoading(false);
      }
    };
    loadCost();
  }, [id]);

  const handleReExtract = async () => {
    if (!id || !reExtractReason.trim()) return;
    setIsReExtracting(true);
    try {
      await adminApi.reExtract(id, reExtractReason);
      alert('Re-extraction job queued successfully');
      setReExtractReason('');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to queue re-extraction');
    } finally {
      setIsReExtracting(false);
    }
  };

  const handleCopyText = async (text: string, label: string) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        alert(`${label} copied to clipboard.`);
        return;
      }
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', 'true');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (!success) {
        throw new Error('Clipboard copy blocked');
      }
      alert(`${label} copied to clipboard.`);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to copy to clipboard');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processed':
        return 'bg-success/20 text-success';
      case 'rejected':
        return 'bg-ember/20 text-ember';
      case 'pending':
        return 'bg-amber-500/20 text-amber-400';
      case 'error':
        return 'bg-ember/20 text-ember';
      default:
        return 'bg-stone/30 text-mist';
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="text-mist animate-pulse">Loading letter...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-ember mb-4">{error}</div>
        <Link to="/admin/letters-in" className="text-rune hover:underline">
          Back to Inbound Mail
        </Link>
      </div>
    );
  }

  if (!letter) return null;

  // Extract metadata
  const extractionMeta =
    extraction?.extraction && typeof extraction.extraction === 'object'
      ? (extraction.extraction as Record<string, unknown>)
      : null;

  const extractionData =
    extractionMeta?.extraction && typeof extractionMeta.extraction === 'object'
      ? (extractionMeta.extraction as Record<string, unknown>)
      : extractionMeta;

  const connectionEnrichmentMeta =
    extractionMeta?.connection_enrichment &&
    typeof extractionMeta.connection_enrichment === 'object'
      ? (extractionMeta.connection_enrichment as Record<string, unknown>)
      : null;

  const summariesMeta =
    extractionMeta?.summaries && typeof extractionMeta.summaries === 'object'
      ? (extractionMeta.summaries as Record<string, unknown>)
      : null;

  const correspondentProfileMeta =
    extractionMeta?.correspondent_profile &&
    typeof extractionMeta.correspondent_profile === 'object'
      ? (extractionMeta.correspondent_profile as Record<string, unknown>)
      : null;

  const correspondentInnerDialogMeta =
    extractionMeta?.correspondent_inner_dialog &&
    typeof extractionMeta.correspondent_inner_dialog === 'object'
      ? (extractionMeta.correspondent_inner_dialog as Record<string, unknown>)
      : null;

  const worldBuilderMeta =
    extractionMeta?.world_builder && typeof extractionMeta.world_builder === 'object'
      ? (extractionMeta.world_builder as Record<string, unknown>)
      : null;

  const worldDeepenMeta =
    extractionMeta?.world_deepen && typeof extractionMeta.world_deepen === 'object'
      ? (extractionMeta.world_deepen as Record<string, unknown>)
      : null;

  const extractionError = (() => {
    const direct = typeof extraction?.errorReason === 'string' ? extraction.errorReason.trim() : '';
    if (direct) return direct;
    if (extractionMeta?.error && typeof extractionMeta.error === 'object') {
      const message = (extractionMeta.error as Record<string, unknown>).message;
      if (typeof message === 'string' && message.trim()) return message.trim();
    }
    return null;
  })();

  // Build pipeline stages
  const pipelineStages: PipelineStageData[] = [];

  if (extractionData) {
    const extractUsage = extractionData.usage as Record<string, unknown> | undefined;
    pipelineStages.push({
      id: 'extraction',
      title: 'Extract World Update',
      description: 'Extract entities, events, and world changes from inbound letter',
      model: String(extractionData.model ?? extractionMeta?.model ?? 'unknown'),
      status: extractionData.status === 'error' ? 'error' : 'success',
      output: extractionData.delta ?? extractionMeta?.delta,
      outputLabel: 'World Update Delta',
      metrics: {
        inputTokens:
          typeof extractUsage?.prompt_tokens === 'number' ? extractUsage.prompt_tokens : undefined,
        outputTokens:
          typeof extractUsage?.completion_tokens === 'number'
            ? extractUsage.completion_tokens
            : undefined,
      },
    });
  }

  if (correspondentProfileMeta) {
    const profileUsage = correspondentProfileMeta.usage as Record<string, unknown> | undefined;
    pipelineStages.push({
      id: 'correspondent-profile',
      title: 'Correspondent Profile',
      description: 'Update understanding of the correspondent based on their letter',
      model: String(correspondentProfileMeta.model ?? 'unknown'),
      status: 'success',
      output: correspondentProfileMeta.profile,
      outputLabel: 'Profile Update',
      metrics: {
        inputTokens:
          typeof profileUsage?.prompt_tokens === 'number' ? profileUsage.prompt_tokens : undefined,
        outputTokens:
          typeof profileUsage?.completion_tokens === 'number'
            ? profileUsage.completion_tokens
            : undefined,
      },
    });
  }

  if (correspondentInnerDialogMeta) {
    const dialogUsage = correspondentInnerDialogMeta.usage as Record<string, unknown> | undefined;
    pipelineStages.push({
      id: 'inner-dialog',
      title: 'Inner Dialog',
      description: "Generate correspondent's internal thoughts and reactions",
      model: String(correspondentInnerDialogMeta.model ?? 'unknown'),
      status: 'success',
      output: correspondentInnerDialogMeta.dialog,
      outputLabel: 'Inner Dialog',
      metrics: {
        inputTokens:
          typeof dialogUsage?.prompt_tokens === 'number' ? dialogUsage.prompt_tokens : undefined,
        outputTokens:
          typeof dialogUsage?.completion_tokens === 'number'
            ? dialogUsage.completion_tokens
            : undefined,
      },
    });
  }

  if (connectionEnrichmentMeta) {
    const connUsage = connectionEnrichmentMeta.usage as Record<string, unknown> | undefined;
    pipelineStages.push({
      id: 'connection-enrichment',
      title: 'Connection Enrichment',
      description: 'Infer relationships and threads from the inbound letter',
      model: String(connectionEnrichmentMeta.model ?? 'unknown'),
      status: 'success',
      output: {
        relationshipsAdded: connectionEnrichmentMeta.relationshipsAdded,
        threadsAdded: connectionEnrichmentMeta.threadsAdded,
      },
      metrics: {
        inputTokens:
          typeof connUsage?.prompt_tokens === 'number' ? connUsage.prompt_tokens : undefined,
        outputTokens:
          typeof connUsage?.completion_tokens === 'number'
            ? connUsage.completion_tokens
            : undefined,
      },
    });
  }

  if (summariesMeta) {
    const sumUsage = summariesMeta.usage as Record<string, unknown> | undefined;
    pipelineStages.push({
      id: 'summaries',
      title: 'Summaries',
      description: 'Regenerate world briefs after extraction',
      model: String(summariesMeta.model ?? 'unknown'),
      status: 'success',
      output: summariesMeta,
      metrics: {
        inputTokens:
          typeof sumUsage?.prompt_tokens === 'number' ? sumUsage.prompt_tokens : undefined,
        outputTokens:
          typeof sumUsage?.completion_tokens === 'number' ? sumUsage.completion_tokens : undefined,
      },
    });
  }

  if (worldBuilderMeta) {
    const wbUsage = worldBuilderMeta.usage as Record<string, unknown> | undefined;
    pipelineStages.push({
      id: 'world-builder',
      title: 'World Builder',
      description: 'World updates derived from the letter',
      model: String(worldBuilderMeta.model ?? 'unknown'),
      status: 'success',
      output: worldBuilderMeta.delta,
      metrics: {
        inputTokens:
          typeof wbUsage?.prompt_tokens === 'number' ? wbUsage.prompt_tokens : undefined,
        outputTokens:
          typeof wbUsage?.completion_tokens === 'number' ? wbUsage.completion_tokens : undefined,
        durationMs:
          typeof worldBuilderMeta.durationMs === 'number' ? worldBuilderMeta.durationMs : undefined,
      },
    });
  }

  if (worldDeepenMeta) {
    const wdUsage = worldDeepenMeta.usage as Record<string, unknown> | undefined;
    pipelineStages.push({
      id: 'world-deepen',
      title: 'World Deepen',
      description: 'Additional world texture layered in after evolution',
      model: String(worldDeepenMeta.model ?? 'unknown'),
      status: 'success',
      output: worldDeepenMeta.delta,
      metrics: {
        inputTokens:
          typeof wdUsage?.prompt_tokens === 'number' ? wdUsage.prompt_tokens : undefined,
        outputTokens:
          typeof wdUsage?.completion_tokens === 'number' ? wdUsage.completion_tokens : undefined,
      },
    });
  }

  // Cost stages
  const extractStages = (detail: typeof costDetail) => {
    if (!detail) return [];
    if (detail.stages && detail.stages.length > 0) {
      return detail.stages.map((s) => ({
        stage: s.stage,
        cost: s.cost,
        costFormatted: `$${s.cost.toFixed(4)}`,
        model: s.model,
        inputTokens: s.inputTokens,
        outputTokens: s.outputTokens,
        latencyMs: s.latencyMs,
      }));
    }
    const events = (
      detail as {
        events?: Array<{
          feature: string;
          cost: number;
          model: string;
          inputTokens: number;
          outputTokens: number;
          latencyMs: number;
        }>;
      }
    )?.events;
    if (events && events.length > 0) {
      return events.map((e) => ({
        stage: e.feature,
        cost: e.cost,
        costFormatted: `$${e.cost.toFixed(4)}`,
        model: e.model,
        inputTokens: e.inputTokens,
        outputTokens: e.outputTokens,
        latencyMs: e.latencyMs,
      }));
    }
    return [];
  };

  const costStages = extractStages(costDetail);
  const hasPipeline = pipelineStages.length > 0;
  const hasCost = costDetail || isCostLoading || costNotFound;
  const hasDelta = !!(extractionMeta?.delta || extractionData?.delta);
  const hasMergeReport = !!extraction?.mergeReport;
  const totalDurationMs = pipelineStages.reduce(
    (sum, s) => sum + (s.metrics?.durationMs ?? 0),
    0,
  );
  const totalTokens = pipelineStages.reduce(
    (sum, s) => sum + (s.metrics?.inputTokens ?? 0) + (s.metrics?.outputTokens ?? 0),
    0,
  );

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="text-sm text-mist">
        <Link to="/admin/letters-in" className="hover:text-parchment">
          Inbound Mail
        </Link>
        <span className="mx-2">/</span>
        <span className="text-parchment font-mono">{letter.id}</span>
      </div>

      {/* Header */}
      <div className="bg-night border border-stone/30 rounded-xl p-6">
        {letter.isQa && (
          <div className="mb-4 rounded-lg border border-rune/40 bg-rune/10 px-3 py-2 text-xs text-rune">
            QA Mail
          </div>
        )}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="font-display text-xl text-parchment mb-1">
              {letter.subject ?? <span className="text-mist/50 italic">No subject</span>}
            </h1>
            <div className="text-mist text-sm">
              From: <span className="text-parchment">{letter.fromEmail}</span>
            </div>
          </div>
          <span className={`px-3 py-1 rounded text-sm ${getStatusColor(letter.status)}`}>
            {letter.status}
          </span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-mist mb-1">World</div>
            <Link to={`/admin/worlds/${letter.worldId}`} className="text-rune hover:underline">
              {letter.worldAddress}
            </Link>
          </div>
          <div>
            <div className="text-mist mb-1">User</div>
            <Link to={`/admin/users/${letter.user.id}`} className="text-rune hover:underline">
              {letter.user.email}
            </Link>
          </div>
          <div>
            <div className="text-mist mb-1">Received</div>
            <div className="text-parchment">{formatDate(letter.receivedAt)}</div>
          </div>
          <div>
            <div className="text-mist mb-1">Processed</div>
            <div className="text-parchment">
              {letter.processedAt ? formatDate(letter.processedAt) : '-'}
            </div>
          </div>
        </div>

        {/* Link to outbound letter that answered this */}
        {letter.answeredByLetterId && (
          <div className="mt-4 p-3 bg-rune/10 border border-rune/30 rounded-lg">
            <div className="flex items-center gap-2 text-sm">
              <Send size={14} className="text-rune" />
              <span className="text-mist">Answered by:</span>
              <Link
                to={`/admin/letters-out/${letter.answeredByLetterId}`}
                className="text-rune hover:underline font-mono"
              >
                {letter.answeredByLetterId}
              </Link>
            </div>
          </div>
        )}

        {letter.rejectedReason && (
          <div className="mt-4 p-3 bg-ember/10 border border-ember/30 rounded-lg">
            <div className="text-ember text-sm font-medium mb-1 flex items-center gap-2">
              <AlertTriangle size={14} />
              Rejection Reason
            </div>
            <div className="text-ember/80 text-sm">{letter.rejectedReason}</div>
          </div>
        )}

        {extractionError && (
          <div className="mt-4 p-3 bg-ember/10 border border-ember/30 rounded-lg">
            <div className="text-ember text-sm font-medium mb-1 flex items-center gap-2">
              <AlertTriangle size={14} />
              Extraction Error
            </div>
            <div className="text-ember/80 text-sm whitespace-pre-wrap">{extractionError}</div>
          </div>
        )}
      </div>

      {/* Tabbed Interface */}
      <div className="bg-night border border-stone/30 rounded-xl overflow-hidden">
        {/* Tab Navigation */}
        <div className="flex flex-wrap border-b border-stone/30 bg-void/30">
          <TabButton
            active={activeTab === 'letter'}
            onClick={() => setActiveTab('letter')}
            icon={<BookOpen size={14} />}
            label="Letter"
          />
          {hasPipeline && (
            <TabButton
              active={activeTab === 'pipeline'}
              onClick={() => setActiveTab('pipeline')}
              icon={<GitBranch size={14} />}
              label="Pipeline"
              badge={`${pipelineStages.length}`}
            />
          )}
          {hasCost && (
            <TabButton
              active={activeTab === 'cost'}
              onClick={() => setActiveTab('cost')}
              icon={<DollarSign size={14} />}
              label="Cost"
              badge={costDetail ? costDetail.totalCostFormatted : undefined}
            />
          )}
          {hasDelta && (
            <TabButton
              active={activeTab === 'delta'}
              onClick={() => setActiveTab('delta')}
              icon={<Code size={14} />}
              label="Delta"
            />
          )}
          {hasMergeReport && (
            <TabButton
              active={activeTab === 'merge'}
              onClick={() => setActiveTab('merge')}
              icon={<GitBranch size={14} />}
              label="Merge"
            />
          )}
          <TabButton
            active={activeTab === 'raw'}
            onClick={() => setActiveTab('raw')}
            icon={<FileText size={14} />}
            label="Raw"
          />
          <TabButton
            active={activeTab === 'actions'}
            onClick={() => setActiveTab('actions')}
            icon={<Wrench size={14} />}
            label="Actions"
          />
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'letter' && (
            <LetterInspector
              bodyText={letter.cleanText}
              draftText={null}
              polishText={null}
              polishSkipped={null}
              letterView="final"
              onViewChange={() => {}}
              onCopyText={handleCopyText}
              subject={letter.subject ?? undefined}
            />
          )}

          {activeTab === 'pipeline' && hasPipeline && (
            <PipelineTimeline
              stages={pipelineStages}
              hasRepair={false}
              totalDurationMs={totalDurationMs}
              totalTokens={totalTokens}
              model={String(extractionMeta?.model ?? 'unknown')}
              utilityModel={null}
            />
          )}

          {activeTab === 'cost' && (
            <div className="space-y-6">
              {costDetail && (
                <CostBreakdown
                  totalCost={costDetail.totalCost}
                  totalCostFormatted={costDetail.totalCostFormatted}
                  totalLatencyMs={costDetail.totalLatencyMs ?? 0}
                  stages={costStages}
                />
              )}
              {isCostLoading && (
                <div className="text-sm text-mist animate-pulse">Loading cost data...</div>
              )}
              {costNotFound && !isCostLoading && !costDetail && (
                <div>
                  <div className="flex items-center gap-2 text-sm text-mist">
                    <DollarSign size={16} className="text-mist/50" />
                    <span>No cost data recorded for this letter</span>
                  </div>
                  <p className="text-xs text-mist/60 mt-2">
                    Cost tracking may not have been enabled when this letter was processed.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'delta' && hasDelta && (
            <div>
              <h3 className="font-display text-lg text-parchment mb-4">Extracted Delta</h3>
              <pre className="p-4 bg-void rounded-lg text-xs text-mist overflow-auto max-h-[600px]">
                {JSON.stringify(extractionMeta?.delta ?? extractionData?.delta, null, 2)}
              </pre>
            </div>
          )}

          {activeTab === 'merge' && hasMergeReport && (
            <div>
              <h3 className="font-display text-lg text-parchment mb-4">Merge Report</h3>
              {extraction?.worldStateVersion && (
                <div className="text-sm text-mist mb-4">
                  World State Version:{' '}
                  <span className="text-parchment">{extraction.worldStateVersion}</span>
                  {extraction.diffCreatedAt && (
                    <span className="ml-4">
                      Created:{' '}
                      <span className="text-parchment">{formatDate(extraction.diffCreatedAt)}</span>
                    </span>
                  )}
                </div>
              )}
              <pre className="p-4 bg-void rounded-lg text-xs text-mist overflow-auto max-h-[600px]">
                {JSON.stringify(extraction?.mergeReport, null, 2)}
              </pre>
            </div>
          )}

          {activeTab === 'raw' && (
            <div>
              <h3 className="font-display text-lg text-parchment mb-4">Raw RFC822</h3>
              <pre className="p-4 bg-void rounded-lg text-mist text-xs font-mono whitespace-pre-wrap overflow-auto max-h-[600px]">
                {letter.rawRfc822}
              </pre>
            </div>
          )}

          {activeTab === 'actions' && (
            <div className="space-y-6">
              <div>
                <h3 className="font-display text-lg text-parchment mb-4">Re-extract Letter</h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={reExtractReason}
                    onChange={(e) => setReExtractReason(e.target.value)}
                    placeholder="Reason for re-extraction..."
                    className="flex-1 px-3 py-2 bg-void border border-stone/30 rounded-lg text-parchment text-sm focus:outline-none focus:border-rune"
                  />
                  <button
                    onClick={handleReExtract}
                    disabled={isReExtracting || !reExtractReason.trim()}
                    className="px-4 py-2 bg-rune/20 text-rune border border-rune/30 rounded-lg text-sm hover:bg-rune/30 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isReExtracting ? 'Queueing...' : 'Re-extract'}
                  </button>
                </div>
              </div>

              {/* Metadata */}
              <div>
                <h3 className="font-display text-lg text-parchment mb-4">Metadata</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-mist mb-1">Letter ID</div>
                    <div className="text-parchment font-mono text-xs">{letter.id}</div>
                  </div>
                  <div>
                    <div className="text-mist mb-1">Message ID</div>
                    <div className="text-parchment font-mono text-xs">{letter.messageId ?? '-'}</div>
                  </div>
                  <div>
                    <div className="text-mist mb-1">Hash</div>
                    <div className="text-parchment font-mono text-xs">{letter.hash}</div>
                  </div>
                  <div>
                    <div className="text-mist mb-1">Inbound Day</div>
                    <div className="text-parchment">{letter.inboundDay.split('T')[0]}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
}

// Tab button component
function TabButton({
  active,
  onClick,
  icon,
  label,
  badge,
  badgeColor = 'rune',
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  badge?: string;
  badgeColor?: 'rune' | 'amber' | 'ember';
}) {
  const badgeColors = {
    rune: 'text-rune bg-rune/10',
    amber: 'text-amber-400 bg-amber-400/10',
    ember: 'text-ember bg-ember/10',
  };

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
        active
          ? 'text-parchment border-rune bg-night/50'
          : 'text-mist hover:text-parchment border-transparent hover:bg-night/30'
      }`}
    >
      {icon}
      <span>{label}</span>
      {badge && (
        <span className={`text-xs px-1.5 py-0.5 rounded ${badgeColors[badgeColor]}`}>{badge}</span>
      )}
    </button>
  );
}
